import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Box, Stack, Typography, IconButton, Chip, Button, Collapse, Fade, useTheme } from '@mui/material'
import {
  ExpandMore,
  ExpandLess,
  Close,
  CheckCircle,
  Warning,
  Info,
  Build,
  RocketLaunch,
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material'
import { useAnnouncements } from '@/src/context/AnnouncementContext'
import { useAuth } from '@/src/hooks/useAuth'
import DeploymentModal from './DeploymentModal'
import DeploymentCountdown from './DeploymentCountdown'

// Helper function to format announcement message with proper rich text rendering
const formatAnnouncementMessage = (message: string) => {
  if (!message) return ''

  // For now, we'll handle basic formatting since the rich text editor converts to plain text
  // In the future, we can enhance this to handle HTML content from the rich text editor

  // Split by line breaks to preserve structure
  const lines = message.split('\n')

  return lines.map((line, index) => {
    const trimmedLine = line.trim()

    // Handle headers (## text)
    if (trimmedLine.startsWith('## ')) {
      return (
        <Typography
          key={index}
          variant='h6'
          sx={{
            fontWeight: 600,
            color: 'inherit',
            mt: index > 0 ? 2 : 0,
            mb: 1
          }}
        >
          {trimmedLine.replace('## ', '')}
        </Typography>
      )
    }

    // Handle numbered lists (1. item or 1️⃣ item)
    if (/^[\d\s]+[\.\s]/.test(trimmedLine) || trimmedLine.match(/^[1-9]️⃣/)) {
      return (
        <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mt: index > 0 ? 1 : 0, mb: 0.5 }}>
          <Typography component='span' sx={{ mr: 1, mt: 0.5, fontWeight: 600, minWidth: '24px' }}>
            {trimmedLine.match(/^([\d\s]+[\.\s]|[1-9]️⃣)/)?.[0] || '•'}
          </Typography>
          <Typography
            component='span'
            variant='body1'
            sx={{
              color: 'inherit',
              lineHeight: 1.6,
              flex: 1
            }}
          >
            {trimmedLine.replace(/^[\d\s]+[\.\s]|[1-9]️⃣/, '').trim()}
          </Typography>
        </Box>
      )
    }

    // Handle bullet points (- item or * item)
    if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
      return (
        <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mt: index > 0 ? 1 : 0, mb: 0.5 }}>
          <Typography component='span' sx={{ mr: 1, mt: 0.5 }}>
            •
          </Typography>
          <Typography
            component='span'
            variant='body1'
            sx={{
              color: 'inherit',
              lineHeight: 1.6,
              flex: 1
            }}
          >
            {trimmedLine.replace(/^[-*]\s/, '')}
          </Typography>
        </Box>
      )
    }

    // Handle empty lines (add spacing)
    if (trimmedLine === '') {
      return <Box key={index} sx={{ height: '8px' }} />
    }

    // Regular paragraph
    return (
      <Typography
        key={index}
        variant='body1'
        sx={{
          color: 'inherit',
          mb: index < lines.length - 1 ? 1 : 0,
          lineHeight: 1.6
        }}
      >
        {trimmedLine}
      </Typography>
    )
  })
}

interface AnnouncementBannerProps {
  showMultiple?: boolean
}

const AnnouncementBanner: React.FC<AnnouncementBannerProps> = ({ showMultiple = false }) => {
  const theme = useTheme()
  const { activeAnnouncements, dismissAnnouncement, acknowledgeAnnouncement, recordInteraction } = useAnnouncements()
  const auth = useAuth()

  // Note: WebSocket connection is handled by Resto365Announcements component
  // This component only displays announcements

  // State for expanded announcements
  const [expandedAnnouncements, setExpandedAnnouncements] = useState<Set<number>>(new Set())

  // State for carousel navigation
  const [currentAnnouncementIndex, setCurrentAnnouncementIndex] = useState(0)

  // State for auto-rotation
  const [isHovering, setIsHovering] = useState(false)

  // Safety check: ensure activeAnnouncements is always an array
  // Use useMemo to prevent unnecessary re-renders and satisfy hook dependencies
  const safeActiveAnnouncements = useMemo(() => activeAnnouncements || [], [activeAnnouncements])

  // Check for deployment announcements
  const deploymentAnnouncement = safeActiveAnnouncements.find(
    announcement => announcement.type === 'deployment' && announcement.isActive
  )

  // Check if user is admin (roleId = 1)
  const isAdmin = auth.user?.role?.id === 1

  // Show deployment modal for non-admin users
  const showDeploymentModal = deploymentAnnouncement && !isAdmin

  // For admin users, show all announcements including deployment ones
  // For non-admin users, filter out deployment announcements (since they see the modal)
  let announcementsToShow = isAdmin
    ? showMultiple
      ? safeActiveAnnouncements
      : safeActiveAnnouncements.slice(0, 1)
    : showMultiple
      ? safeActiveAnnouncements.filter(a => a.type !== 'deployment')
      : safeActiveAnnouncements.filter(a => a.type !== 'deployment').slice(0, 1)

  // Sort announcements by priority, displayOrder, type, and dateTime
  if (announcementsToShow.length > 1) {
    announcementsToShow = [...announcementsToShow].sort((a, b) => {
      // 1. Priority (high > medium > low)
      const priorityOrder = { high: 3, medium: 2, low: 1 }
      const priorityDiff =
        (priorityOrder[a.priority as keyof typeof priorityOrder] || 0) -
        (priorityOrder[b.priority as keyof typeof priorityOrder] || 0)
      if (priorityDiff !== 0) return -priorityDiff // High priority first

      // 2. Display order (lower number comes first)
      const displayOrderDiff = (a.displayOrder || 0) - (b.displayOrder || 0)
      if (displayOrderDiff !== 0) return displayOrderDiff

      // 3. Type (deployment > error > warning > maintenance > info)
      const typeOrder = { deployment: 5, error: 4, warning: 3, maintenance: 2, info: 1 }
      const typeDiff =
        (typeOrder[a.type as keyof typeof typeOrder] || 0) - (typeOrder[b.type as keyof typeof typeOrder] || 0)
      if (typeDiff !== 0) return -typeDiff // Higher type priority first

      // 4. Start date (earlier start date comes first)
      if (a.startDate && b.startDate) {
        const startDateDiff = new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
        if (startDateDiff !== 0) return startDateDiff
      }

      // 5. End date (earlier end date comes first)
      if (a.endDate && b.endDate) {
        const endDateDiff = new Date(a.endDate).getTime() - new Date(b.endDate).getTime()
        if (endDateDiff !== 0) return endDateDiff
      }

      return 0
    })
  }

  // Reset current index when announcements change
  useEffect(() => {
    setCurrentAnnouncementIndex(0)
  }, [safeActiveAnnouncements])

  // Safety check: ensure currentAnnouncementIndex is within bounds
  useEffect(() => {
    if (announcementsToShow.length > 0 && currentAnnouncementIndex >= announcementsToShow.length) {
      setCurrentAnnouncementIndex(0)
    }
  }, [announcementsToShow.length, currentAnnouncementIndex])

  // Note: We don't automatically record "viewed" interactions on page load
  // Only record interactions when user explicitly interacts (expand, dismiss, acknowledge)

  // Get icon based on announcement type
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'info':
        return <Info />
      case 'warning':
        return <Warning />
      case 'error':
        return <Close />
      case 'maintenance':
        return <Build />
      case 'deployment':
        return <RocketLaunch />
      default:
        return <Info />
    }
  }

  // Get color based on priority
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'error'
      case 'medium':
        return 'warning'
      case 'low':
        return 'info'
      default:
        return 'info'
    }
  }

  // Get severity color for type
  const getSeverityColor = (type: string) => {
    switch (type) {
      case 'error':
        return 'error'
      case 'warning':
        return 'warning'
      case 'maintenance':
        return 'warning'
      case 'deployment':
        return 'info'
      default:
        return 'info'
    }
  }

  // Get icon background color based on announcement type (consistent with icon)
  const getIconBackgroundColor = (type: string) => {
    switch (type) {
      case 'error':
        return 'error'
      case 'warning':
        return 'warning'
      case 'maintenance':
        return 'warning'
      case 'deployment':
        return 'info'
      case 'info':
        return 'info'
      default:
        return 'info'
    }
  }

  // Toggle announcement expansion
  const handleToggle = (id: number) => {
    setExpandedAnnouncements(prev => {
      const newSet = new Set(prev)
      const isCurrentlyExpanded = newSet.has(id)

      if (isCurrentlyExpanded) {
        newSet.delete(id)

        // Record collapse interaction
        recordInteraction(id, 'collapsed')
      } else {
        newSet.add(id)

        // Record expand interaction
        recordInteraction(id, 'expanded')
      }

      return newSet
    })
  }

  // Handle announcement dismissal
  const handleDismiss = (id: number) => {
    dismissAnnouncement(id)
  }

  // Handle announcement acknowledgment
  const handleAcknowledge = (id: number) => {
    acknowledgeAnnouncement(id)
  }

  // Navigation functions
  const goToNext = useCallback(() => {
    if (announcementsToShow.length > 1) {
      setCurrentAnnouncementIndex(prev => (prev + 1) % announcementsToShow.length)
    }
  }, [announcementsToShow.length])

  const goToPrevious = useCallback(() => {
    if (announcementsToShow.length > 1) {
      setCurrentAnnouncementIndex(prev => (prev - 1 + announcementsToShow.length) % announcementsToShow.length)
    }
  }, [announcementsToShow.length])

  const goToAnnouncement = useCallback((index: number) => {
    setCurrentAnnouncementIndex(index)
  }, [])

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (announcementsToShow.length <= 1) return

      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        goToPrevious()
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        goToNext()
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [announcementsToShow.length, goToNext, goToPrevious])

  // Auto-rotation effect
  useEffect(() => {
    if (announcementsToShow.length <= 1 || isHovering) {
      return
    }

    const interval = setInterval(() => {
      // Safety check: ensure we have valid announcements before rotating
      if (announcementsToShow.length > 0) {
        setCurrentAnnouncementIndex(prev => (prev + 1) % announcementsToShow.length)
      }
    }, 5000) // 5 seconds

    return () => clearInterval(interval)
  }, [announcementsToShow.length, isHovering])

  // Mouse event handlers for banner hover
  const handleMouseEnter = useCallback(() => {
    setIsHovering(true)
  }, [])

  const handleMouseLeave = useCallback(() => {
    setIsHovering(false)
  }, [])

  // Don't render if no active announcements (after all hooks)
  if (safeActiveAnnouncements.length === 0) {
    return null
  }

  // Format date for display in Sydney timezone
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      timeZone: 'Australia/Sydney',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // For non-admin users, if there's a deployment announcement, show only the modal
  if (showDeploymentModal) {
    return (
      <>
        <DeploymentModal announcement={deploymentAnnouncement} open={true} />
        {/* Render nothing else - system is locked */}
      </>
    )
  }

  // Safety check: ensure we have valid announcements to show
  if (!announcementsToShow || announcementsToShow.length === 0) {
    return null
  }

  // If only one announcement, show it normally
  if (announcementsToShow.length === 1) {
    const announcement = announcementsToShow[0]
    const isExpanded = expandedAnnouncements.has(announcement.id)
    const severity = getSeverityColor(announcement.type)
    const priorityColor = getPriorityColor(announcement.priority)

    return (
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: theme.zIndex.appBar + 1,
          width: '100%'
        }}
      >
        <Stack spacing={2} sx={{ p: 2 }}>
          <Box
            sx={{
              backgroundColor: theme.palette.grey[50],
              borderRadius: 3,
              p: 2,
              boxShadow: 'none',
              border: `1px solid ${theme.palette[severity].main}`,
              position: 'relative'
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {/* Header with icon and title */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  backgroundColor: theme.palette[getIconBackgroundColor(announcement.type)].main
                }}
              >
                {getTypeIcon(announcement.type)}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant='h6' sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  {announcement.title}
                </Typography>

                {/* Connection Status Indicator - removed for cleaner UI */}
                {/* Real-time updates are working automatically in the background */}
                <Chip
                  label={announcement.type.charAt(0).toUpperCase() + announcement.type.slice(1)}
                  size='small'
                  sx={{
                    fontWeight: 500,
                    fontSize: '10px',
                    lineHeight: '14px',
                    color: theme.palette[severity].main,
                    borderRadius: '4px',
                    backgroundColor: theme.palette.grey[100],
                    height: 20
                  }}
                />
              </Box>

              {/* Right side actions */}
              <Stack direction='row' spacing={1} alignItems='center'>
                {/* Priority badge */}
                <Chip
                  label={announcement.priority}
                  size='small'
                  sx={{
                    fontWeight: 500,
                    fontSize: '10px',
                    lineHeight: '14px',
                    color: theme.palette[priorityColor].main,
                    borderRadius: '4px',
                    backgroundColor: theme.palette.grey[100],
                    height: 24
                  }}
                />

                {/* Expand/Collapse button */}
                {announcement.isCollapsible && (
                  <IconButton
                    size='small'
                    onClick={() => handleToggle(announcement.id)}
                    sx={{
                      color: theme.palette.text.primary,
                      '&:hover': {
                        backgroundColor: theme.palette[severity].main,
                        color: 'white'
                      }
                    }}
                  >
                    {isExpanded ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                )}

                {/* Dismiss button - only show if dismissible AND doesn't require acknowledgement AND not deployment */}
                {announcement.isDismissible &&
                  !announcement.requiresAcknowledgment &&
                  announcement.type !== 'deployment' && (
                    <IconButton
                      size='small'
                      onClick={() => handleDismiss(announcement.id)}
                      sx={{
                        color: theme.palette.text.primary,
                        '&:hover': {
                          backgroundColor: theme.palette[severity].main,
                          color: 'white'
                        }
                      }}
                    >
                      <Close />
                    </IconButton>
                  )}

                {/* Show disabled dismiss button if acknowledgement is required */}
                {announcement.isDismissible && announcement.requiresAcknowledgment && (
                  <IconButton
                    size='small'
                    disabled
                    sx={{
                      color: theme.palette.text.disabled,
                      cursor: 'not-allowed',
                      '&:hover': {
                        backgroundColor: 'transparent'
                      }
                    }}
                    title='Cannot dismiss - acknowledgement required'
                  >
                    <Close />
                  </IconButton>
                )}

                {/* Show disabled dismiss button if deployment type */}
                {announcement.isDismissible && announcement.type === 'deployment' && (
                  <IconButton
                    size='small'
                    disabled
                    sx={{
                      color: theme.palette.text.disabled,
                      cursor: 'not-allowed',
                      '&:hover': {
                        backgroundColor: 'transparent'
                      }
                    }}
                    title='Cannot dismiss - deployment announcements cannot be dismissed'
                  >
                    <Close />
                  </IconButton>
                )}
              </Stack>
            </Box>

            {/* Content */}
            <Collapse in={isExpanded || !announcement.isCollapsible}>
              <Box sx={{ color: theme.palette.text.primary, mb: 2 }}>
                {formatAnnouncementMessage(announcement.message)}
              </Box>

              {/* Additional details - Only show date/time for deployment type announcements */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 2 }}>
                {announcement.type === 'deployment' && announcement.startDate && (
                  <Typography variant='caption' sx={{ color: theme.palette.text.secondary }}>
                    <strong>From:</strong> {formatDate(announcement.startDate)}
                  </Typography>
                )}
                {announcement.type === 'deployment' && announcement.endDate && (
                  <Typography variant='caption' sx={{ color: theme.palette.text.secondary }}>
                    <strong>Until:</strong> {formatDate(announcement.endDate)}
                  </Typography>
                )}
              </Box>

              {/* Countdown Timer for Deployment Announcements */}
              {announcement.type === 'deployment' && announcement.endDate && (
                <Box sx={{ mt: 2 }}>
                  <DeploymentCountdown
                    endDate={announcement.endDate}
                    onComplete={() => {
                      // Refresh announcements when countdown completes
                      // This will be handled by the real-time service
                    }}
                  />
                </Box>
              )}

              {/* Action buttons */}
              {announcement.requiresAcknowledgment && (
                <Box sx={{ mt: 2 }}>
                  <Button
                    size='small'
                    variant='contained'
                    startIcon={<CheckCircle />}
                    onClick={() => handleAcknowledge(announcement.id)}
                    sx={{
                      backgroundColor: theme.palette.success.main,
                      color: 'white',
                      '&:hover': {
                        backgroundColor: theme.palette.success.dark
                      }
                    }}
                  >
                    Acknowledge
                  </Button>
                </Box>
              )}
            </Collapse>

            {/* Collapse hint */}
            {announcement.isCollapsible && !isExpanded && (
              <Typography
                variant='caption'
                sx={{
                  color: theme.palette.text.secondary,
                  fontStyle: 'italic',
                  cursor: 'pointer',
                  mt: 1,
                  display: 'block'
                }}
                onClick={() => handleToggle(announcement.id)}
              >
                Click to expand for more details
              </Typography>
            )}
          </Box>
        </Stack>
      </Box>
    )
  }

  // Multi-announcement carousel view
  const currentAnnouncement = announcementsToShow[currentAnnouncementIndex]

  // Safety check: if no current announcement, don't render
  if (!currentAnnouncement) {
    return null
  }

  const isExpanded = expandedAnnouncements.has(currentAnnouncement.id)
  const severity = getSeverityColor(currentAnnouncement.type)
  const priorityColor = getPriorityColor(currentAnnouncement.priority)

  return (
    <Box
      sx={{
        position: 'relative',
        zIndex: 0,
        width: '100%'
      }}
    >
      <Stack spacing={2} sx={{ p: 2 }}>
        {/* Carousel Container */}
        <Box
          sx={{
            backgroundColor: theme.palette.grey[50],
            borderRadius: 3,
            p: 2,
            boxShadow: 'none',
            border: `1px solid ${theme.palette[severity].main}`,
            position: 'relative'
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {/* Announcement Content */}
          <Fade in={true} timeout={300}>
            <Box>
              {/* Header with icon and title */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor: theme.palette[getIconBackgroundColor(currentAnnouncement.type)].main
                  }}
                >
                  {getTypeIcon(currentAnnouncement.type)}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant='h6' sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                    {currentAnnouncement.title}
                  </Typography>
                  <Chip
                    label={currentAnnouncement.type.charAt(0).toUpperCase() + currentAnnouncement.type.slice(1)}
                    size='small'
                    sx={{
                      fontWeight: 500,
                      fontSize: '10px',
                      lineHeight: '14px',
                      color: theme.palette[severity].main,
                      borderRadius: '4px',
                      backgroundColor: theme.palette.grey[100],
                      height: 20
                    }}
                  />
                </Box>

                {/* Right side actions */}
                <Stack direction='row' spacing={1} alignItems='center'>
                  {/* Priority badge */}
                  <Chip
                    label={currentAnnouncement.priority}
                    size='small'
                    sx={{
                      fontWeight: 500,
                      fontSize: '10px',
                      lineHeight: '14px',
                      color: theme.palette[priorityColor].main,
                      borderRadius: '4px',
                      backgroundColor: theme.palette.grey[100],
                      height: 24
                    }}
                  />

                  {/* Expand/Collapse button */}
                  {currentAnnouncement.isCollapsible && (
                    <IconButton
                      size='small'
                      onClick={() => handleToggle(currentAnnouncement.id)}
                      sx={{
                        color: theme.palette.text.primary,
                        '&:hover': {
                          backgroundColor: theme.palette[severity].main,
                          color: 'white'
                        }
                      }}
                    >
                      {isExpanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  )}

                  {/* Dismiss button - only show if dismissible AND doesn't require acknowledgement AND not deployment */}
                  {currentAnnouncement.isDismissible &&
                    !currentAnnouncement.requiresAcknowledgment &&
                    currentAnnouncement.type !== 'deployment' && (
                      <IconButton
                        size='small'
                        onClick={() => handleDismiss(currentAnnouncement.id)}
                        sx={{
                          color: theme.palette.text.primary,
                          '&:hover': {
                            backgroundColor: theme.palette[severity].main,
                            color: 'white'
                          }
                        }}
                      >
                        <Close />
                      </IconButton>
                    )}

                  {/* Show disabled dismiss button if acknowledgement is required */}
                  {currentAnnouncement.isDismissible && currentAnnouncement.requiresAcknowledgment && (
                    <IconButton
                      size='small'
                      disabled
                      sx={{
                        color: theme.palette.text.disabled,
                        cursor: 'not-allowed',
                        '&:hover': {
                          backgroundColor: 'transparent'
                        }
                      }}
                      title='Cannot dismiss - acknowledgement required'
                    >
                      <Close />
                    </IconButton>
                  )}

                  {/* Show disabled dismiss button if deployment type */}
                  {currentAnnouncement.isDismissible && currentAnnouncement.type === 'deployment' && (
                    <IconButton
                      size='small'
                      disabled
                      sx={{
                        color: theme.palette.text.disabled,
                        cursor: 'not-allowed',
                        '&:hover': {
                          backgroundColor: 'transparent'
                        }
                      }}
                      title='Cannot dismiss - deployment announcements cannot be dismissed'
                    >
                      <Close />
                    </IconButton>
                  )}
                </Stack>
              </Box>

              {/* Content */}
              <Collapse in={isExpanded || !currentAnnouncement.isCollapsible}>
                <Box sx={{ color: theme.palette.text.primary, mb: 2 }}>
                  {formatAnnouncementMessage(currentAnnouncement.message)}
                </Box>

                {/* Additional details - Only show date/time for deployment type announcements */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 2 }}>
                  {currentAnnouncement.type === 'deployment' && currentAnnouncement.startDate && (
                    <Typography variant='caption' sx={{ color: theme.palette.text.secondary }}>
                      <strong>From:</strong> {formatDate(currentAnnouncement.startDate)}
                    </Typography>
                  )}
                  {currentAnnouncement.type === 'deployment' && currentAnnouncement.endDate && (
                    <Typography variant='caption' sx={{ color: theme.palette.text.secondary }}>
                      <strong>Until:</strong> {formatDate(currentAnnouncement.endDate)}
                    </Typography>
                  )}
                </Box>

                {/* Countdown Timer for Deployment Announcements in Carousel */}
                {currentAnnouncement.type === 'deployment' && currentAnnouncement.endDate && (
                  <Box sx={{ mt: 2 }}>
                    <DeploymentCountdown
                      endDate={currentAnnouncement.endDate}
                      onComplete={() => {
                        // This will be handled by the real-time service
                      }}
                    />
                  </Box>
                )}

                {/* Action buttons */}
                {currentAnnouncement.requiresAcknowledgment && (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      size='small'
                      variant='contained'
                      startIcon={<CheckCircle />}
                      onClick={() => handleAcknowledge(currentAnnouncement.id)}
                      sx={{
                        backgroundColor: theme.palette.success.main,
                        color: 'white',
                        '&:hover': {
                          backgroundColor: theme.palette.success.dark
                        }
                      }}
                    >
                      Acknowledge
                    </Button>
                  </Box>
                )}
              </Collapse>

              {/* Collapse hint */}
              {currentAnnouncement.isCollapsible && !isExpanded && (
                <Typography
                  variant='caption'
                  sx={{
                    color: theme.palette.text.secondary,
                    fontStyle: 'italic',
                    cursor: 'pointer',
                    mt: 1,
                    display: 'block'
                  }}
                  onClick={() => handleToggle(currentAnnouncement.id)}
                >
                  Click to expand for more details
                </Typography>
              )}
            </Box>
          </Fade>

          {/* Navigation Footer */}
          {announcementsToShow.length > 1 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mt: 1,
                pt: 1,
                pb: 0.5,
                borderTop: `1px solid ${theme.palette.divider}`
              }}
            >
              {/* Left Arrow */}
              <IconButton
                onClick={goToPrevious}
                size='small'
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover
                  }
                }}
              >
                <ChevronLeft />
              </IconButton>

              {/* Center: Dot Indicators and Counter */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {/* Dot Indicators */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {announcementsToShow.map((_, index) => (
                    <Box
                      key={index}
                      onClick={() => goToAnnouncement(index)}
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor:
                          index === currentAnnouncementIndex ? theme.palette[severity].main : theme.palette.divider,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor:
                            index === currentAnnouncementIndex
                              ? theme.palette[severity].dark
                              : theme.palette.action.hover
                        }
                      }}
                    />
                  ))}
                </Box>

                {/* Counter */}
                <Typography variant='caption' sx={{ fontWeight: 500, color: theme.palette.text.secondary }}>
                  {currentAnnouncementIndex + 1} of {announcementsToShow.length}
                </Typography>
              </Box>

              {/* Right Arrow */}
              <IconButton
                onClick={goToNext}
                size='small'
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover
                  }
                }}
              >
                <ChevronRight />
              </IconButton>
            </Box>
          )}
        </Box>
      </Stack>
    </Box>
  )
}

export default AnnouncementBanner
